import { Ellipsis } from 'lucide-react'
import * as React from 'react'
import { Card, CardHeader, CardTitle } from '@/components/ui/card'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useTranslation } from '@/hooks/useTranslation'
import { cn } from '@/lib/utils'

export interface ProjectTileProps extends React.HTMLAttributes<HTMLDivElement> {
	title: string
	authorName: string
	tilesVisible: boolean
	projectId: string
	onRename: (projectId: string) => void | Promise<void>
	onDelete: (projectId: string) => void | Promise<void>
	disableActions?: boolean
	canRename?: boolean
	canDelete?: boolean
}

const ProjectTile = React.forwardRef<HTMLDivElement, ProjectTileProps>(
	(
		{
			title,
			authorName,
			tilesVisible,
			projectId,
			onRename,
			onDelete,
			disableActions = false,
			canRename = false,
			canDelete = false,
			...props
		},
		ref
	) => {
		const { t } = useTranslation()
		const showActionsMenu = !disableActions && (canRename || canDelete)

		return (
			<Card
				className={cn(
					`hover:shadow-lg hover:scale-105 transition-all cursor-pointer duration-700 ease-out ${tilesVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3'}`,
					props.className
				)}
				ref={ref}
				{...props}
			>
				<CardHeader className="flex flex-row items-start justify-between">
					<div>
						<CardTitle>{title}</CardTitle>

						<p className="text-sm text-gray-500">
							{t('projectTile.author')} {authorName}
						</p>
					</div>

					{showActionsMenu && (
						<DropdownMenu>
							<DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
								<button
									type="button"
									aria-label={t('projectTile.openActions')}
									className="rounded p-1 transition-all duration-300 ease-in-out hover:bg-gray-100 focus:outline-none"
								>
									<Ellipsis className="h-5 w-5" />
								</button>
							</DropdownMenuTrigger>

							<DropdownMenuContent
								align="end"
								side="bottom"
								className="w-36"
								onClick={(e) => e.stopPropagation()}
							>
								{canRename && (
									<DropdownMenuItem onSelect={() => onRename(projectId)}>
										{t('projectTile.rename')}
									</DropdownMenuItem>
								)}

								{canDelete && (
									<DropdownMenuItem variant="destructive" onSelect={() => onDelete(projectId)}>
										{t('projectTile.delete')}
									</DropdownMenuItem>
								)}
							</DropdownMenuContent>
						</DropdownMenu>
					)}
				</CardHeader>
			</Card>
		)
	}
)
ProjectTile.displayName = 'ProjectTile'

export { ProjectTile }
